import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DataGrid,
  gridPageCountSelector,
  gridPageSelector,
  useGridApiContext,
  useGridApiRef,
  useGridSelector,
} from "@mui/x-data-grid";
import { Button, TextField } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import Pagination from "@mui/material/Pagination";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format } from "date-fns";
import classes from "../LandedCostUploadHistoryTable/LandedCostUploadHistory.module.css";
import { ensurePercentSuffix } from "../utils/currency";
import { exportRowsToXlsx } from "../utils/exportUtils";

const normalizeTariffRow = (row = {}, index = 0) => {
  const id =
    row.seqId ?? row.id ?? row.uuid ?? row.recordId ?? row.hts ?? `tariff-${index}`;
  const cooRaw = row.coo ?? row.countryOfOrigin ?? row.country_of_origin ?? "";
  const htsRaw = row.hts ?? row.htsCode ?? row.hts_code ?? "";
  return {
    id,
    ...row,
    hts: typeof htsRaw === "string" ? htsRaw.trim() : String(htsRaw || ""),
    coo: typeof cooRaw === "string" ? cooRaw.trim().toUpperCase() : String(cooRaw || ""),
    sumOfTariffAndDuties:
      row.sumOfTariffAndDuties ??
      row.sum_tariff_duties ??
      row.totalTariffAndDuties ??
      row.total_tariff_and_duties ??
      "",
    date: row.date ?? row.effectiveDate ?? row.effective_date ?? "",
    duty: row.duty ?? row.dutyRate ?? row.duty_rate ?? "",
    tariffSec122Percent:
      row.tariffSec122Percent ??
      row.tariff1Percent ??
      row.tariff1 ??
      row.tariff_1 ??
      row.tariffPct1 ??
      row.tariff1pct ??
      "",
    tariffSec232Percent:
      row.tariffSec232Percent ??
      row.tariff2Percent ??
      row.tariff2 ??
      row.tariff_2 ??
      row.tariffPct2 ??
      row.tariff2pct ??
      "",
    tariffSec301Percent:
      row.tariffSec301Percent ??
      row.tariff3Percent ??
      row.tariff3 ??
      row.tariff_3 ??
      row.tariffPct3 ??
      row.tariff3pct ??
      "",
  };
};

const LandedCostTariffTable = ({ rows = [], loading = false }) => {
  const [pageSize, setPageSize] = useState(15);
  const [isEditing, setIsEditing] = useState(false);
  const saving = false;
  const [gridRows, setGridRows] = useState([]);
  const dirtyRowsRef = useRef(new Map());
  const apiRef = useGridApiRef();

  useEffect(() => {
    const normalizedRows = Array.isArray(rows) ? rows.map(normalizeTariffRow) : [];
    setGridRows(normalizedRows);
    dirtyRowsRef.current.clear();
    setIsEditing(false);
  }, [rows]);

  const markRowDirty = useCallback((newRow, oldRow) => {
    if (!newRow || !oldRow) return;
    const changed =
      newRow.hts !== oldRow.hts ||
      newRow.coo !== oldRow.coo ||
      newRow.sumOfTariffAndDuties !== oldRow.sumOfTariffAndDuties ||
      newRow.date !== oldRow.date ||
      newRow.duty !== oldRow.duty ||
      newRow.tariffSec122Percent !== oldRow.tariffSec122Percent ||
      newRow.tariffSec232Percent !== oldRow.tariffSec232Percent ||
      newRow.tariffSec301Percent !== oldRow.tariffSec301Percent;

    if (changed) {
      dirtyRowsRef.current.set(newRow.id, newRow);
    } else {
      dirtyRowsRef.current.delete(newRow.id);
    }
  }, []);

  const handleProcessRowUpdate = useCallback(
    (newRow, oldRow) => {
      if (!isEditing) return oldRow;
      setGridRows((prev) =>
        prev.map((row) => (row.id === newRow.id ? newRow : row))
      );
      markRowDirty(newRow, oldRow);
      return newRow;
    },
    [isEditing, markRowDirty]
  );

  const CustomDateFilterInput = (props) => {
    const { item, applyValue } = props;
    const [selectedDate, setSelectedDate] = useState(
      item.value ? new Date(item.value) : null
    );

    const handleChange = (newDate) => {
      const formattedDate = newDate ? format(newDate, "yyyy-MM-dd") : null;
      setSelectedDate(newDate);
      applyValue({ ...item, value: formattedDate });
    };

    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DatePicker
          label="Select Date"
          value={selectedDate}
          onChange={(newDate) => handleChange(newDate)}
          views={["year", "month", "day"]}
          renderInput={(params) => <TextField {...params} fullWidth />}
        />
      </LocalizationProvider>
    );
  };

  const CustomDateRangeFilterInput = (props) => {
    const { item, applyValue } = props;
    const [selectedDateRangeFrom, setSelectedDateRangeFrom] = useState(
      item.value?.from ? new Date(item.value.from) : null
    );
    const [selectedDateRangeTo, setSelectedDateRangeTo] = useState(
      item.value?.to ? new Date(item.value.to) : null
    );

    const handleChange = (newFrom, newTo) => {
      if (newFrom || newTo) {
        applyValue({
          ...item,
          value: {
            from: newFrom ? format(newFrom, "yyyy-MM-dd") : null,
            to: newTo ? format(newTo, "yyyy-MM-dd") : null,
          },
        });
      }
    };

    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <DatePicker
            label="From Date"
            value={selectedDateRangeFrom}
            onChange={(newDate) => {
              setSelectedDateRangeFrom(newDate);
              handleChange(newDate, selectedDateRangeTo);
            }}
            views={["year", "month", "day"]}
            renderInput={(params) => <TextField {...params} fullWidth />}
          />
          <DatePicker
            label="To Date"
            value={selectedDateRangeTo}
            onChange={(newDate) => {
              setSelectedDateRangeTo(newDate);
              handleChange(selectedDateRangeFrom, newDate);
            }}
            views={["year", "month", "day"]}
            renderInput={(params) => <TextField {...params} fullWidth />}
          />
        </div>
      </LocalizationProvider>
    );
  };

  const dateFilterOperators = useMemo(
    () => [
      {
        label: "is",
        value: "is",
        getApplyFilterFn: (filterItem) => {
          if (!filterItem.columnField || !filterItem.value) {
            return null;
          }
          const filterDate = new Date(filterItem.value);
          return ({ value }) => {
            const cellDate = new Date(value);
            return (
              cellDate.getFullYear() === filterDate.getFullYear() &&
              cellDate.getMonth() === filterDate.getMonth() &&
              cellDate.getDate() === filterDate.getDate()
            );
          };
        },
        InputComponent: CustomDateFilterInput,
      },
      {
        label: "is after",
        value: "is after",
        getApplyFilterFn: (filterItem) => {
          if (!filterItem.columnField || !filterItem.value) {
            return null;
          }
          return ({ value }) => new Date(value) > new Date(filterItem.value);
        },
        InputComponent: CustomDateFilterInput,
      },
      {
        label: "is before",
        value: "is before",
        getApplyFilterFn: (filterItem) => {
          if (!filterItem.columnField || !filterItem.value) {
            return null;
          }
          return ({ value }) => new Date(value) < new Date(filterItem.value);
        },
        InputComponent: CustomDateFilterInput,
      },
      {
        label: "date range",
        value: "date range",
        getApplyFilterFn: (filterItem) => {
          if (!filterItem.columnField || !filterItem.value) {
            return null;
          }

          const { from, to } = filterItem.value;
          const fromDate = from ? new Date(from) : null;
          const toDate = to ? new Date(to) : null;

          return ({ value }) => {
            const cellDate = new Date(value);
            if (fromDate && toDate) {
              return cellDate >= fromDate && cellDate <= toDate;
            }
            if (fromDate) return cellDate >= fromDate;
            if (toDate) return cellDate <= toDate;
            return true;
          };
        },
        InputComponent: CustomDateRangeFilterInput,
      },
    ],
    []
  );

  const columns = useMemo(() => {
    const dateOperators = dateFilterOperators;
    return [
      { field: "hts", headerName: "HTS", flex: 1, minWidth: 120, editable: true },
      { field: "coo", headerName: "COO", flex: 1, minWidth: 120, editable: true },
      {
        field: "sumOfTariffAndDuties",
        headerName: "Sum of Tariff & Duties",
        flex: 1.4,
        minWidth: 200,
        editable: true,
        valueFormatter: ({ value }) => ensurePercentSuffix(value),
      },
      {
        field: "date",
        headerName: "Date",
        flex: 1,
        minWidth: 120,
        editable: true,
        filterOperators: dateOperators,
      },
      {
        field: "duty",
        headerName: "Duty",
        flex: 1,
        minWidth: 120,
        editable: true,
        valueFormatter: ({ value }) => ensurePercentSuffix(value),
      },
      {
        field: "tariffSec122Percent",
        headerName: "Tariff Sec 122%",
        flex: 1,
        minWidth: 150,
        editable: true,
        valueFormatter: ({ value }) => ensurePercentSuffix(value),
      },
      {
        field: "tariffSec232Percent",
        headerName: "Tariff Sec 232%",
        flex: 1,
        minWidth: 150,
        editable: true,
        valueFormatter: ({ value }) => ensurePercentSuffix(value),
      },
      {
        field: "tariffSec301Percent",
        headerName: "Tariff Sec 301%",
        flex: 1,
        minWidth: 150,
        editable: true,
        valueFormatter: ({ value }) => ensurePercentSuffix(value),
      },
    ];
  }, [dateFilterOperators]);

  const handleExport = useCallback(() => {
    exportRowsToXlsx({
      columns,
      rows: gridRows,
      fileName: "tariff-data.xlsx",
      sheetName: "Tariff Data",
      warningMessage: "No tariff data to export.",
    });
  }, [columns, gridRows]);

  const CustomPagination = () => {
    const apiRef = useGridApiContext();
    const page = useGridSelector(apiRef, gridPageSelector);
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);

    return (
      <Pagination
        color="primary"
        count={pageCount}
        page={page + 1}
        onChange={(_, value) => apiRef.current.setPage(value - 1)}
      />
    );
  };

  return (
    <div className={classes.wrapper}>
      <div className={classes.tableActions}>
        <Button
          variant="contained"
          size="small"
          onClick={handleExport}
          disabled={!gridRows.length || loading}
          startIcon={<DownloadIcon fontSize="small" />}
          className={classes.exportButton}
          data-testid="tariff-export-btn"
        >
          Export
        </Button>
      </div>
      <DataGrid
        rows={gridRows}
        columns={columns}
        apiRef={apiRef}
        disableRowSelectionOnClick
        hideFooterSelectedRowCount
        pageSize={pageSize}
        rowsPerPageOptions={[15, 25, 50]}
        onPageSizeChange={(size) => setPageSize(size)}
        editMode="cell"
        autoHeight
        isCellEditable={() => false}
        processRowUpdate={handleProcessRowUpdate}
        experimentalFeatures={{ newEditingApi: true }}
        onProcessRowUpdateError={(error) => console.error(error)}
        rowHeight={32}
        columnHeaderHeight={36}
        loading={loading || saving}
        components={{ Pagination: CustomPagination }}
        sx={{
          "& .MuiDataGrid-cell": {
            fontSize: "0.78rem",
            py: 0.25,
          },
          "& .MuiDataGrid-columnHeaders": {
            fontSize: "0.82rem",
          },
        }}
      />
    </div>
  );
};

export default LandedCostTariffTable;
